import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "./supabase/auth";
import {
  Ingredient,
  saveIngredient,
  PROTEIN_CLASSES,
  PROTEIN_CLASS_LABELS,
  toProteinClass,
} from "./helpers/saveIngredient";
import { getDataSource } from "./helpers/getDataSource";
import ColorPickerField from "./ColorPickerField";
import { useTranslation } from 'react-i18next';

const mono = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const sectionBox: React.CSSProperties = {
  border: "1px solid #d0d0d0",
  background: "#ffffff",
  marginBottom: "16px",
};

const sectionTitle: React.CSSProperties = {
  padding: "6px 10px",
  fontSize: "11px",
  fontWeight: 700,
  textTransform: "uppercase",
  background: "#f0f0f0",
  borderBottom: "1px solid #d0d0d0",
  color: "#000000",
};

const fieldRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  borderBottom: "1px solid #d0d0d0",
  fontSize: "12px",
};

const fieldLabelStyle: React.CSSProperties = {
  width: "180px",
  minWidth: "180px",
  padding: "4px 10px",
  background: "#f8f8f8",
  fontWeight: 600,
  color: "#333333",
  borderRight: "1px solid #d0d0d0",
};

const inputBase: React.CSSProperties = {
  flex: 1,
  padding: "3px 8px",
  fontSize: "12px",
  fontFamily: mono,
  border: "1px solid #d0d0d0",
  background: "#ffffff",
  color: "#000000",
  outline: "none",
  margin: "2px 4px",
};

const changedBg = "#fff9db";

const btnStyle: React.CSSProperties = {
  padding: "4px 14px",
  fontSize: "12px",
  fontWeight: 600,
  border: "1px solid #d0d0d0",
  background: "#f0f0f0",
  cursor: "pointer",
  color: "#000000",
};

const errorStyle: React.CSSProperties = { color: "#cc0000", fontSize: "11px", marginLeft: "8px" };
const warnStyle: React.CSSProperties = { color: "#996600", fontSize: "11px", background: "#fff9db", padding: "1px 4px", marginLeft: "8px" };

interface FormState {
  id: string;
  common_name: string;
  korean_name: string;
  fda_name: string;
  supplier: string;
  color: string;
  tags: string[];
  /** "" means unset, saved as null. */
  protein_class: string;
  protein_per_100g: string;
  digestibility: string;
  fat: string;
  satfat: string;
  transfat: string;
  cholesterol: string;
  carbs: string;
  fiber: string;
  sugar: string;
  sodium: string;
  moisture: string;
  cost_per_kg: string;
  aa_his: string;
  aa_ile: string;
  aa_leu: string;
  aa_lys: string;
  aa_mc: string;
  aa_pt: string;
  aa_thr: string;
  aa_trp: string;
  aa_val: string;
  vitamin_d: string;
  calcium: string;
  iron: string;
  potassium: string;
  sub_ingredients: string;
  notes_spec_ref: string;
  // Fatty acids (g/100g)
  fa_ala: string;
  fa_la: string;
  fa_oleic: string;
  fa_palmitic: string;
  fa_stearic: string;
  // Fiber fractions (g/100g)
  fiber_insoluble: string;
  fiber_soluble: string;
  resistant_starch: string;
  ash: string;
  // BCAA (derived, read-only)
  bcaa_mg_per_g: string;
  // Reference digestibility
  ref_digest_product: string;
  ref_digest_value: string;
  ref_digest_source: string;
  // Per-field data sources
  data_sources: Record<string, string>;
}

function ingredientToForm(ing: Ingredient): FormState {
  const s = (v: unknown) => v == null ? "" : String(v);
  return {
    id: ing.id,
    common_name: ing.common_name || "",
    korean_name: ing.korean_name || "",
    fda_name: ing.fda_name || "",
    supplier: ing.supplier || "",
    color: ing.color || "",
    tags: ing.tags || [],
    protein_class: ing.protein_class || "",
    protein_per_100g: s(ing.protein_per_100g),
    digestibility: s(ing.digestibility),
    fat: s(ing.fat),
    satfat: s(ing.satfat),
    transfat: s(ing.transfat),
    cholesterol: s(ing.cholesterol),
    carbs: s(ing.carbs),
    fiber: s(ing.fiber),
    sugar: s(ing.sugar),
    sodium: s(ing.sodium),
    moisture: s(ing.moisture),
    cost_per_kg: s(ing.cost_per_kg),
    aa_his: s(ing.aa_his),
    aa_ile: s(ing.aa_ile),
    aa_leu: s(ing.aa_leu),
    aa_lys: s(ing.aa_lys),
    aa_mc: s(ing.aa_mc),
    aa_pt: s(ing.aa_pt),
    aa_thr: s(ing.aa_thr),
    aa_trp: s(ing.aa_trp),
    aa_val: s(ing.aa_val),
    vitamin_d: s(ing.vitamin_d),
    calcium: s(ing.calcium),
    iron: s(ing.iron),
    potassium: s(ing.potassium),
    sub_ingredients: ing.sub_ingredients || "",
    notes_spec_ref: ing.notes_spec_ref || "",
    fa_ala: s(ing.fa_ala),
    fa_la: s(ing.fa_la),
    fa_oleic: s(ing.fa_oleic),
    fa_palmitic: s(ing.fa_palmitic),
    fa_stearic: s(ing.fa_stearic),
    fiber_insoluble: s(ing.fiber_insoluble),
    fiber_soluble: s(ing.fiber_soluble),
    resistant_starch: s(ing.resistant_starch),
    ash: s((ing as any).ash),
    bcaa_mg_per_g: s(ing.bcaa_mg_per_g),
    ref_digest_product: ing.ref_digest_product || "",
    ref_digest_value: s(ing.ref_digest_value),
    ref_digest_source: ing.ref_digest_source || "",
    data_sources: ing.data_sources ? { ...ing.data_sources } : {},
  };
}

function formToIngredient(f: FormState): Partial<Ingredient> {
  // Non-finite results (junk from paste, "1e999") become null, not NaN — NaN
  // passes every range check (all comparisons false) and saves as null or a 400.
  const num = (v: string) => {
    if (v.trim() === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    id: f.id,
    common_name: f.common_name,
    korean_name: f.korean_name || null,
    fda_name: f.fda_name || null,
    supplier: f.supplier || null,
    color: f.color || null,
    tags: f.tags,
    // Blank stays null. The column is nullable and must never receive "".
    protein_class: toProteinClass(f.protein_class),
    protein_per_100g: num(f.protein_per_100g),
    digestibility: num(f.digestibility),
    fat: num(f.fat),
    satfat: num(f.satfat),
    transfat: num(f.transfat),
    cholesterol: num(f.cholesterol),
    carbs: num(f.carbs),
    fiber: num(f.fiber),
    sugar: num(f.sugar),
    sodium: num(f.sodium),
    moisture: num(f.moisture),
    cost_per_kg: num(f.cost_per_kg),
    aa_his: num(f.aa_his),
    aa_ile: num(f.aa_ile),
    aa_leu: num(f.aa_leu),
    aa_lys: num(f.aa_lys),
    aa_mc: num(f.aa_mc),
    aa_pt: num(f.aa_pt),
    aa_thr: num(f.aa_thr),
    aa_trp: num(f.aa_trp),
    aa_val: num(f.aa_val),
    vitamin_d: num(f.vitamin_d),
    calcium: num(f.calcium),
    iron: num(f.iron),
    potassium: num(f.potassium),
    sub_ingredients: f.sub_ingredients || null,
    notes_spec_ref: f.notes_spec_ref || null,
    fa_ala: num(f.fa_ala),
    fa_la: num(f.fa_la),
    fa_oleic: num(f.fa_oleic),
    fa_palmitic: num(f.fa_palmitic),
    fa_stearic: num(f.fa_stearic),
    fiber_insoluble: num(f.fiber_insoluble),
    fiber_soluble: num(f.fiber_soluble),
    resistant_starch: num(f.resistant_starch),
    ash: num(f.ash),
    bcaa_mg_per_g: (() => {
      const l = num(f.aa_leu), i = num(f.aa_ile), v = num(f.aa_val);
      return (l != null || i != null || v != null) ? (l ?? 0) + (i ?? 0) + (v ?? 0) : null;
    })(),
    ref_digest_product: f.ref_digest_product || null,
    ref_digest_value: num(f.ref_digest_value),
    ref_digest_source: f.ref_digest_source || null,
    data_sources: Object.keys(f.data_sources).length > 0 ? f.data_sources : null,
  };
}

function TagInput({ tags, onChange, placeholder }: { tags: string[]; onChange: (t: string[]) => void; placeholder?: string }) {
  const [input, setInput] = useState("");

  const addTag = (raw: string) => {
    const t = raw.trim();
    if (!t || tags.some((x) => x.toLowerCase() === t.toLowerCase())) return;
    onChange([...tags, t]);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(input);
      setInput("");
    }
    if (e.key === "Backspace" && input === "" && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px", flex: 1, padding: "2px 4px" }}>
      {tags.map((t, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", padding: "1px 6px", background: "#e8e8e8", border: "1px solid #ccc", fontSize: "11px", gap: "4px" }}>
          {t}
          <span style={{ cursor: "pointer", fontWeight: 700, color: "#666" }} onClick={() => onChange(tags.filter((_, j) => j !== i))}>x</span>
        </span>
      ))}
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKey}
        placeholder={tags.length === 0 ? (placeholder || "") : ""}
        style={{ ...inputBase, border: "none", margin: 0, minWidth: "100px", flex: 1 }}
      />
    </div>
  );
}

export default function IngredientEditScreen({
  ingredientId,
  onBack,
  onSaved,
}: {
  ingredientId: string;
  onBack: () => void;
  onSaved: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "ko" ? "ko" : "en";
  const [originalRow, setOriginalRow] = useState<Ingredient | null>(null);
  const [initialForm, setInitialForm] = useState<FormState | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [savedId, setSavedId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("ingredients").select("*").eq("id", ingredientId).single();
      if (data) {
        const ing = data as Ingredient;
        setOriginalRow(ing);
        const f = ingredientToForm(ing);
        setInitialForm(f);
        setForm(f);
      }
      setLoading(false);
    })();
  }, [ingredientId]);

  if (loading || !form || !initialForm) {
    return <div style={{ color: "#666", padding: "20px", textAlign: "center", fontSize: "12px" }}>{t('common.loading')}</div>;
  }
  if (!originalRow) {
    return <div style={{ color: "#666", padding: "20px", textAlign: "center", fontSize: "12px" }}>{t('ingredientDetail.notFound')}</div>;
  }

  const set = (field: keyof FormState) => (val: string) => {
    setForm((f) => {
      if (!f) return f;
      const next = { ...f, [field]: val };
      // Auto-recalculate BCAA when any of leu/ile/val change
      if (field === "aa_leu" || field === "aa_ile" || field === "aa_val") {
        const l = next.aa_leu.trim() === "" ? null : Number(next.aa_leu);
        const i = next.aa_ile.trim() === "" ? null : Number(next.aa_ile);
        const v = next.aa_val.trim() === "" ? null : Number(next.aa_val);
        next.bcaa_mg_per_g = (l != null || i != null || v != null)
          ? String((l ?? 0) + (i ?? 0) + (v ?? 0))
          : "";
      }
      return next;
    });
  };

  const isChanged = (field: keyof FormState): boolean => {
    if (field === "tags") {
      return JSON.stringify(form.tags) !== JSON.stringify(initialForm.tags);
    }
    return (form as any)[field] !== (initialForm as any)[field];
  };

  const getInputStyle = (field: keyof FormState, readOnly?: boolean): React.CSSProperties => ({
    ...inputBase,
    ...(isChanged(field) ? { background: changedBg } : {}),
    ...(readOnly ? { background: "#f0f0f0" } : {}),
  });

  const handleSave = async () => {
    setSaving(true);
    setErrors([]);
    setWarnings([]);

    const { data: userData } = await supabase.auth.getUser();
    const email = userData?.user?.email || "unknown";

    const result = await saveIngredient(formToIngredient(form), "edit", originalRow, email);
    setSaving(false);

    if (!result.success) {
      setErrors(result.errors);
      setWarnings(result.warnings);
      return;
    }
    if (result.warnings.length > 0) {
      // Partial failure (e.g. change-log write failed) or "No changes detected":
      // keep the screen mounted so the warning is seen; navigate only on
      // explicit continue.
      setWarnings(result.warnings);
      setSavedId(result.data!.id);
      return;
    }
    onSaved(result.data!.id);
  };

  return (
    <div style={{ background: "#ffffff", padding: "8px", minHeight: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
        <button style={btnStyle} onClick={onBack}>{t('ingredientForm.cancel')}</button>
        <span style={{ fontSize: "14px", fontWeight: 700, color: "#000" }}>{t('ingredientForm.edit')} {originalRow.common_name}</span>
        <span style={{ fontSize: "12px", color: "#666", fontFamily: mono }}>{originalRow.id}</span>
        <button style={{ ...btnStyle, marginLeft: "auto", background: "#e0e0e0", fontWeight: 700 }} onClick={handleSave} disabled={saving}>
          {saving ? t('ingredientForm.saving') : t('ingredientForm.save')}
        </button>
      </div>

      {errors.length > 0 && (
        <div style={{ border: "1px solid #cc0000", background: "#fff0f0", padding: "8px", marginBottom: "12px", fontSize: "12px", color: "#cc0000" }}>
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}
      {warnings.length > 0 && (
        <div style={{ border: "1px solid #cc9900", background: "#fff9db", padding: "8px", marginBottom: "12px", fontSize: "12px", color: "#996600" }}>
          {warnings.map((w, i) => <div key={i}>{w}</div>)}
          {savedId && (
            <button style={{ ...btnStyle, marginTop: "6px" }} onClick={() => onSaved(savedId)}>
              {t('portal.continue')}
            </button>
          )}
        </div>
      )}

      <div style={{ maxWidth: "800px" }}>
        {/* Identity */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.identity')}</div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.id')}</div>
            <input type="text" value={form.id} readOnly style={{ ...inputBase, background: "#f0f0f0" }} />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.commonName')}</div>
            <input type="text" value={form.common_name} onChange={(e) => set("common_name")(e.target.value)} style={getInputStyle("common_name")} />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.koreanName')}</div>
            <input type="text" value={form.korean_name} onChange={(e) => set("korean_name")(e.target.value)} style={getInputStyle("korean_name")} />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.fdaName')}</div>
            <input type="text" value={form.fda_name} onChange={(e) => set("fda_name")(e.target.value)} style={getInputStyle("fda_name")} />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.supplier')}</div>
            <input type="text" value={form.supplier} onChange={(e) => set("supplier")(e.target.value)} style={getInputStyle("supplier")} />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.color')}</div>
            <ColorPickerField value={form.color} onChange={(v) => set("color")(v)} inputStyle={getInputStyle("color")} />
          </div>
          <div style={{ ...fieldRowStyle, ...(isChanged("tags") ? { background: changedBg } : {}) }}>
            <div style={fieldLabelStyle}>{t('ingredientForm.tags')}</div>
            <TagInput tags={form.tags} onChange={(t) => setForm((f) => f ? { ...f, tags: t } : f)} placeholder={t('ingredientForm.typeAndPressEnter')} />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.proteinClass', { defaultValue: lang === "ko" ? "단백질 분류" : "Protein class" })}</div>
            <select
              value={form.protein_class}
              onChange={(e) => set("protein_class")(e.target.value)}
              style={{ ...getInputStyle("protein_class"), fontFamily: "inherit" }}
            >
              <option value="">-</option>
              {PROTEIN_CLASSES.map((c) => (
                <option key={c} value={c}>
                  {t(PROTEIN_CLASS_LABELS[c].key, { defaultValue: PROTEIN_CLASS_LABELS[c][lang] })}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Macronutrients */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.macronutrients')}</div>
          {([
            [t('ingredientForm.proteinG100g'), "protein_per_100g"],
            [t('ingredientForm.fatLabel'), "fat"],
            [t('ingredientForm.saturatedFat'), "satfat"],
            [t('ingredientForm.transFat'), "transfat"],
            [t('ingredientForm.cholesterol'), "cholesterol"],
            [t('ingredientForm.carbsLabel'), "carbs"],
            [t('ingredientForm.fiberTotal'), "fiber"],
            [t('ingredientForm.sugar'), "sugar"],
            [t('ingredientForm.sodium'), "sodium"],
            [t('ingredientForm.moistureLabel'), "moisture"],
            [t('ingredientForm.ashLabel'), "ash"],
            [t('ingredientForm.costPerKg'), "cost_per_kg"],
          ] as [string, keyof FormState][]).map(([label, key]) => (
            <div key={key} style={fieldRowStyle}>
              <div style={fieldLabelStyle}>{label}</div>
              <input type="number" step="any" value={(form as any)[key]} onChange={(e) => set(key)(e.target.value)} style={getInputStyle(key)} />
            </div>
          ))}
        </div>

        {/* Fatty Acid Profile */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.fattyAcidProfile')}</div>
          {([
            [t('ingredientForm.ala'), "fa_ala"],
            [t('ingredientForm.la'), "fa_la"],
            [t('ingredientForm.oleic'), "fa_oleic"],
            [t('ingredientForm.palmitic'), "fa_palmitic"],
            [t('ingredientForm.stearic'), "fa_stearic"],
          ] as [string, keyof FormState][]).map(([label, key]) => (
            <div key={key} style={fieldRowStyle}>
              <div style={fieldLabelStyle}>{label}</div>
              <input type="number" step="any" value={(form as any)[key]} onChange={(e) => set(key)(e.target.value)} style={getInputStyle(key)} />
            </div>
          ))}
        </div>

        {/* Amino Acids */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.aminoAcids')}</div>
          {([
            [t('ingredientForm.histidine'), "aa_his"], [t('ingredientForm.isoleucine'), "aa_ile"], [t('ingredientForm.leucine'), "aa_leu"],
            [t('ingredientForm.lysine'), "aa_lys"], [t('ingredientForm.metCys'), "aa_mc"], [t('ingredientForm.pheTyr'), "aa_pt"],
            [t('ingredientForm.threonine'), "aa_thr"], [t('ingredientForm.tryptophan'), "aa_trp"], [t('ingredientForm.valine'), "aa_val"],
          ] as [string, keyof FormState][]).map(([label, key]) => (
            <div key={key} style={fieldRowStyle}>
              <div style={fieldLabelStyle}>{label}</div>
              <input type="number" step="any" value={(form as any)[key]} onChange={(e) => set(key)(e.target.value)} style={getInputStyle(key)} />
            </div>
          ))}
          {/* BCAA (derived, read-only) */}
          <div style={{ ...fieldRowStyle, background: "#f8f8ff" }}>
            <div style={{ ...fieldLabelStyle, fontStyle: "italic", color: "#555" }}>{t('ingredientForm.bcaa')}</div>
            <input type="text" value={form.bcaa_mg_per_g ? `${form.bcaa_mg_per_g} mg/g` : "-"} readOnly style={{ ...inputBase, background: "#f0f0f0", color: "#555" }} />
            <span style={{ fontSize: "9px", color: "#999", marginRight: "8px", whiteSpace: "nowrap" }}>{t('ingredientForm.calculated')}</span>
          </div>
        </div>

        {/* Fiber Fractions */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.fiberFractions')}</div>
          <div style={{ ...fieldRowStyle, background: "#f8f8f8" }}>
            <div style={{ ...fieldLabelStyle, fontStyle: "italic" }}>{t('ingredientForm.totalDietaryFiber')}</div>
            <input type="text" value={form.fiber || "-"} readOnly style={{ ...inputBase, background: "#f0f0f0", color: "#555" }} />
            <span style={{ fontSize: "9px", color: "#999", marginRight: "8px", whiteSpace: "nowrap" }}>{t('ingredientForm.editAbove')}</span>
          </div>
          {([
            [t('ingredientForm.insolubleFiber'), "fiber_insoluble"],
            [t('ingredientForm.solubleFiber'), "fiber_soluble"],
            [t('ingredientForm.resistantStarch'), "resistant_starch"],
          ] as [string, keyof FormState][]).map(([label, key]) => (
            <div key={key} style={fieldRowStyle}>
              <div style={fieldLabelStyle}>{label}</div>
              <input type="number" step="any" value={(form as any)[key]} onChange={(e) => set(key)(e.target.value)} style={getInputStyle(key)} />
            </div>
          ))}
        </div>

        {/* Protein Digestibility */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.proteinDigestibility')}</div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.ingredientDigestibility')}</div>
            <input type="number" step="any" min="0" max="1" value={form.digestibility} onChange={(e) => set("digestibility")(e.target.value)} style={getInputStyle("digestibility")} />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.referenceProduct')}</div>
            <input type="text" value={form.ref_digest_product} onChange={(e) => set("ref_digest_product")(e.target.value)} style={getInputStyle("ref_digest_product")} placeholder={t('ingredientForm.caseinReference')} />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.referenceDigestibility')}</div>
            <input type="number" step="any" min="0" max="1" value={form.ref_digest_value} onChange={(e) => set("ref_digest_value")(e.target.value)} style={getInputStyle("ref_digest_value")} />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.referenceCitation')}</div>
            <input type="text" value={form.ref_digest_source} onChange={(e) => set("ref_digest_source")(e.target.value)} style={getInputStyle("ref_digest_source")} placeholder={t('ingredientForm.faoReference')} />
          </div>
        </div>

        {/* Micronutrients */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.micronutrients')}</div>
          {([
            [t('ingredientForm.vitaminD'), "vitamin_d"], [t('ingredientForm.calcium'), "calcium"], [t('ingredientForm.iron'), "iron"], [t('ingredientForm.potassium'), "potassium"],
          ] as [string, keyof FormState][]).map(([label, key]) => (
            <div key={key} style={fieldRowStyle}>
              <div style={fieldLabelStyle}>{label}</div>
              <input type="number" step="any" value={(form as any)[key]} onChange={(e) => set(key)(e.target.value)} style={getInputStyle(key)} />
            </div>
          ))}
        </div>

        {/* Specs */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.specs')}</div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.subIngredients')}</div>
            <textarea
              value={form.sub_ingredients}
              onChange={(e) => set("sub_ingredients")(e.target.value)}
              rows={3}
              style={{ ...getInputStyle("sub_ingredients"), resize: "vertical" }}
            />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.notesSpecRef')}</div>
            <textarea
              value={form.notes_spec_ref}
              onChange={(e) => set("notes_spec_ref")(e.target.value)}
              rows={3}
              style={{ ...getInputStyle("notes_spec_ref"), resize: "vertical" }}
            />
          </div>
        </div>

        {/* Data Sources */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.dataSources')}</div>
          {/* Primary/default source */}
          <div style={fieldRowStyle}>
            <div style={{ ...fieldLabelStyle, fontWeight: 700, color: "#4a4a8a" }}>{t('ingredientForm.defaultSource')}</div>
            <input
              type="text"
              value={form.data_sources["_default"] || ""}
              onChange={(e) => {
                const next = { ...form.data_sources };
                if (e.target.value) {
                  next["_default"] = e.target.value;
                } else {
                  delete next["_default"];
                }
                setForm((f) => f ? { ...f, data_sources: next } : f);
              }}
              placeholder="e.g. USDA FDC #168936"
              style={inputBase}
            />
          </div>
          {/* Field-specific sources */}
          {([
            ["digestibility", t('ingredientForm.proteinDigestibility')],
            ["aa_profile", t('ingredientForm.aminoAcidProfile')],
            ["fatty_acids", t('ingredientForm.fattyAcidProfileShort')],
            ["fiber_fractions", t('ingredientForm.fiberFractions')],
            ["resistant_starch", t('ingredientForm.resistantStarch')],
            ["ref_digestibility", t('ingredientForm.refDigestibility')],
            ["bcaa_mg_per_g", t('ingredientForm.bcaa')],
            ["cost_per_kg", t('ingredientForm.costPerKg')],
            ["protein_per_100g", t('ingredientForm.proteinG100g')],
            ["fiber", t('ingredientForm.fiberLabel')],
            ["fat", t('ingredientForm.fatLabel')],
            ["moisture", t('ingredientForm.moistureLabel')],
          ] as [string, string][]).map(([key, label]) => {
            const resolved = getDataSource(form.data_sources, key);
            const isExplicit = !!form.data_sources[key];
            return (
              <div key={key} style={fieldRowStyle}>
                <div style={fieldLabelStyle}>{label}</div>
                <input
                  type="text"
                  value={form.data_sources[key] || ""}
                  onChange={(e) => {
                    const next = { ...form.data_sources };
                    if (e.target.value) {
                      next[key] = e.target.value;
                    } else {
                      delete next[key];
                    }
                    setForm((f) => f ? { ...f, data_sources: next } : f);
                  }}
                  placeholder={resolved ? `(default: ${resolved})` : "Override source..."}
                  style={{ ...inputBase, ...(isExplicit ? { background: "#f0f0ff" } : {}) }}
                />
              </div>
            );
          })}
          {/* Show all configured source keys */}
          {Object.keys(form.data_sources).filter((k) =>
            !["_default", "digestibility", "aa_profile", "fatty_acids", "fiber_fractions", "resistant_starch", "ref_digestibility", "bcaa_mg_per_g", "cost_per_kg", "protein_per_100g", "fiber", "fat", "moisture"].includes(k)
          ).length > 0 && (
            <>
              <div style={{ ...sectionTitle, background: "#f8f8f8", fontSize: "10px" }}>{t('ingredientForm.additionalSources')}</div>
              {Object.entries(form.data_sources)
                .filter(([k]) => !["_default", "digestibility", "aa_profile", "fatty_acids", "fiber_fractions", "resistant_starch", "ref_digestibility", "bcaa_mg_per_g", "cost_per_kg", "protein_per_100g", "fiber", "fat", "moisture"].includes(k))
                .map(([key, val]) => (
                  <div key={key} style={fieldRowStyle}>
                    <div style={fieldLabelStyle}>{key}</div>
                    <input
                      type="text"
                      value={val}
                      onChange={(e) => {
                        const next = { ...form.data_sources };
                        if (e.target.value) {
                          next[key] = e.target.value;
                        } else {
                          delete next[key];
                        }
                        setForm((f) => f ? { ...f, data_sources: next } : f);
                      }}
                      style={inputBase}
                    />
                  </div>
                ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
